import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo, Section, SectionHead, FadeUp } from "./Ui";
import { COMPANIES, TECHNOLOGY, WHY, NEWS_FALLBACK } from "./data";

export const CompanyCard = ({ c, tall = false }) => (
  <FadeUp className={`col-span-12 ${c.span}`}>
    <article className={`dz-card relative group ${tall ? "h-96" : "h-72 md:h-80"}`} data-testid={`dz-company-${c.id}`}>
      <img src={c.image} alt={`${c.name} — ${c.tagline}`} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700 ease-out" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
      <div className="absolute bottom-0 p-7">
        <div className="dz-label !text-blue-400 mb-2">{c.sector}</div>
        <h3 className="text-xl sm:text-2xl font-medium text-white">{c.name}</h3>
        <p className="mt-2 text-sm text-zinc-300 max-w-lg leading-relaxed">{c.blurb}</p>
        {c.href && <Link to={c.href} data-testid={`dz-company-link-${c.id}`}
          className="inline-block mt-4 text-sm text-blue-400 hover:text-blue-300">Visit platform →</Link>}
      </div>
    </article>
  </FadeUp>
);

export default function DrezoroHome() {
  return (
    <div data-testid="drezoro-home">
      <Seo title="DREZORO — Building the Future of Intelligent Business"
        description="DREZORO is the parent technology company behind Vozelto, TurnKey Property Maintenance, Black Ember Coffee, and Operation Enough — creating AI-powered companies, platforms, and technologies." />

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 dz-grid-bg" aria-hidden="true" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-blue-500/10 blur-[120px] dz-hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <div className="dz-label mb-6">The Parent Technology Company</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl leading-[1.06]">
              Building the Future of <span className="text-blue-400">Intelligent Business</span>.
            </h1>
            <p className="mt-7 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">
              DREZORO creates AI-powered companies, platforms, and technologies that help businesses
              work smarter, grow faster, and operate more efficiently.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/drezoro/companies" className="dz-btn-primary" data-testid="dz-hero-companies">Explore Our Companies</Link>
              <Link to="/drezoro/about" className="dz-btn-secondary" data-testid="dz-hero-vision">Our Vision</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <Section id="who-we-are" className="border-t border-white/5">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <div className="dz-label mb-4">Who We Are</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">
              An innovation group engineering intelligent companies.
            </h2>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
              DREZORO develops intelligent software, AI systems, automation platforms, and modern
              technology businesses. We design, build, and operate companies from the ground up —
              uniting artificial intelligence, cloud software, and disciplined execution.
            </p>
            <p className="mt-4 text-base md:text-lg text-zinc-400 leading-relaxed">
              Our approach is defined by innovation, reliability, and long-term thinking. Every
              platform we create is measured by one standard: the success of the customers and
              businesses it serves.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="dz-card !rounded-2xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1461695008884-244cb4543d74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBiJTI2d3xlbnwwfHx8fDE3ODQyMzY2NDB8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern corporate architecture representing DREZORO's long-term foundations" loading="lazy"
                className="w-full h-[420px] object-cover grayscale" />
            </div>
          </FadeUp>
        </div>
      </Section>

      {/* OUR COMPANIES */}
      <Section id="companies" className="border-t border-white/5">
        <SectionHead label="Our Companies" title="A family of brands, one standard of excellence."
          intro="Each DREZORO company operates independently, powered by shared technology, shared intelligence, and a shared commitment to customers." />
        <div className="grid grid-cols-12 gap-6">
          {COMPANIES.map((c) => <CompanyCard key={c.id} c={c} />)}
        </div>
        <FadeUp className="mt-8"><div className="dz-card border-dashed !bg-transparent p-8 text-center text-zinc-500 text-sm" data-testid="dz-future-companies">
          New companies join the DREZORO group as we build them. This portfolio is designed to grow.
        </div></FadeUp>
      </Section>

      {/* TECHNOLOGY */}
      <Section id="technology" className="border-t border-white/5">
        <SectionHead label="Our Technology" title="Advanced business technology, built in-house." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TECHNOLOGY.map((t, i) => (
            <FadeUp key={t.t} delay={i * 0.05}>
              <div className="dz-card p-6 h-full" data-testid={`dz-tech-${i}`}>
                <div className="w-8 h-px bg-blue-500 mb-5" aria-hidden="true" />
                <h3 className="text-base font-medium text-white">{t.t}</h3>
                <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{t.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Section>

      {/* VISION */}
      <Section id="vision" className="border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 flex justify-center" aria-hidden="true">
          <div className="w-[520px] h-[520px] rounded-full bg-blue-500/[0.07] blur-[110px]" />
        </div>
        <FadeUp className="relative text-center max-w-4xl mx-auto">
          <div className="dz-label mb-6">Our Vision</div>
          <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-snug text-white">
            "Our vision is to build intelligent technology that empowers businesses of every size
            to accomplish more with less effort."
          </blockquote>
        </FadeUp>
      </Section>

      {/* WHY DREZORO */}
      <Section id="why" className="border-t border-white/5">
        <SectionHead label="Why DREZORO" title="The standards behind everything we build." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {WHY.map((w, i) => (
            <FadeUp key={w.t} delay={i * 0.04}>
              <div className="border-t border-white/15 pt-5" data-testid={`dz-why-${i}`}>
                <h3 className="text-base font-medium text-white">{w.t}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{w.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Section>

      {/* NEWS PREVIEW */}
      <Section id="news" className="border-t border-white/5">
        <div className="flex items-end justify-between mb-12">
          <SectionHead label="News & Press" title="From the newsroom." />
          <FadeUp><Link to="/drezoro/news" className="text-sm text-blue-400 hover:text-blue-300 whitespace-nowrap" data-testid="dz-news-all">All announcements →</Link></FadeUp>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {NEWS_FALLBACK.slice(0, 3).map((n) => (
            <FadeUp key={n.id}>
              <article className="dz-card p-6 h-full" data-testid={`dz-news-${n.id}`}>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
                  <span className="text-blue-400 uppercase tracking-wider font-semibold">{n.tag}</span>
                  <time dateTime={n.date}>{n.date}</time>
                </div>
                <h3 className="text-base font-medium leading-snug text-white">{n.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{n.body}</p>
              </article>
            </FadeUp>
          ))}
        </div>
      </Section>

      {/* CONTACT CTA */}
      <Section className="border-t border-white/5">
        <FadeUp className="text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Partner with DREZORO.</h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">Enterprise partnerships, press, and investment enquiries.</p>
          <Link to="/drezoro/contact" className="dz-btn-primary mt-8" data-testid="dz-cta-contact">Contact Us</Link>
        </FadeUp>
      </Section>
    </div>
  );
}
